import React, { useState, useEffect, useRef } from 'react'
import {
  Plus,
  Download,
  Wrench,
  Clock,
  CheckCircle,
  DollarSign
} from 'lucide-react'
import DataTable from '../../components/DataTable/DataTable'
import Breadcrumb from '../../components/Common/Breadcrumb'
import ConfirmationModal from '../../components/ConfirmationModal/ConfirmationModal'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import assetMaintenanceService from '../../services/assetMaintenanceService'
import { formatDate, formatAmount, statusBadgeClass } from './assetUtils'
import '../../components/ConfirmationModal/ConfirmationModal.css'
// Consolidated CSS is imported globally

const AssetMaintenanceList = () => {
  const navigate = useNavigate()
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState({
    totalMaintenance: 0,
    scheduledCount: 0,
    inProgressCount: 0,
    completedCount: 0,
    totalCost: 0
  })
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deleteItem, setDeleteItem] = useState(null)
  const hasLoadedRef = useRef(false)
  const hasShownEmptyToastRef = useRef(false)

  useEffect(() => {
    if (!hasLoadedRef.current) {
      hasLoadedRef.current = true
      loadData()
    }
  }, [])

  const loadData = async () => {
    try {
      setLoading(true)
      const [listResponse, statsResponse] = await Promise.all([
        assetMaintenanceService.getAllMaintenance({ limit: 1000 }),
        assetMaintenanceService.getMaintenanceStats()
      ])

      if (listResponse.success) {
        // Flatten related master names so the table can search and sort on them
        const transformedData = (listResponse.data || []).map(item => ({
          ...item,
          id: item._id,
          assetLabel: item.acquisition?.assetName || item.acquisition?.acquisitionCode || '',
          providerName: item.provider?.providerName || ''
        }))
        setData(transformedData)
        if (listResponse.data?.length === 0 && !hasShownEmptyToastRef.current) {
          hasShownEmptyToastRef.current = true
          toast.info('No maintenance records found. Create your first maintenance record!')
        }
      } else {
        toast.error(listResponse.message || 'Failed to load maintenance records')
      }

      if (statsResponse.success) {
        setStats(statsResponse.data)
      }
    } catch (error) {
      console.error('Error loading maintenance records:', error)
      if (error.response?.status === 401) {
        toast.error('Authentication required. Please log in again.')
      } else if (error.response?.status === 403) {
        toast.error('You do not have permission to view maintenance records')
      } else if (!error._errorHandled) {
        toast.error('Failed to load maintenance records')
      }
    } finally {
      setLoading(false)
    }
  }

  const columns = [
    {
      accessorKey: 'maintenanceCode',
      header: 'Code',
      cell: ({ getValue }) => (
        <div className="service-code-cell" style={{ textAlign: 'left' }}>
          <span className="code-text">{getValue()}</span>
        </div>
      ),
    },
    {
      accessorKey: 'assetLabel',
      header: 'Asset',
      cell: ({ row }) => {
        const acquisition = row.original.acquisition
        return (
          <div className="service-description-cell" style={{ textAlign: 'left', whiteSpace: 'normal', wordWrap: 'break-word' }}>
            <span className="description-text">
              {acquisition ? (acquisition.assetName || acquisition.acquisitionCode || '-') : '-'}
            </span>
          </div>
        )
      },
    },
    {
      accessorKey: 'providerName',
      header: 'Provider',
      cell: ({ row }) => {
        const provider = row.original.provider
        return (
          <div style={{ textAlign: 'left' }}>
            {provider ? (provider.providerName || provider.providerCode || '-') : '-'}
          </div>
        )
      },
    },
    {
      accessorKey: 'maintenanceType',
      header: 'Type',
      cell: ({ getValue }) => (
        <div style={{ textAlign: 'left' }}>
          <span className="status-badge active">{getValue() || '-'}</span>
        </div>
      ),
    },
    {
      accessorKey: 'scheduledDate',
      header: 'Scheduled',
      cell: ({ getValue }) => (
        <div style={{ textAlign: 'left' }}>{formatDate(getValue())}</div>
      ),
    },
    {
      accessorKey: 'completedDate',
      header: 'Completed',
      cell: ({ getValue }) => (
        <div style={{ textAlign: 'left' }}>{formatDate(getValue())}</div>
      ),
    },
    {
      accessorKey: 'cost',
      header: 'Cost',
      cell: ({ row }) => (
        <div style={{ textAlign: 'right' }}>
          {formatAmount(row.original.cost, row.original.currency)}
        </div>
      ),
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ getValue }) => (
        <div className="active-cell" style={{ textAlign: 'center' }}>
          <span className={`status-badge ${statusBadgeClass(getValue())}`}>{getValue() || '-'}</span>
        </div>
      ),
    },
  ]

  const handleAdd = () => {
    navigate('/assets/maintenance/form')
  }

  const handleEdit = (record) => {
    navigate(`/assets/maintenance/edit?id=${record._id}`, { state: { maintenanceData: record } })
  }

  const handleView = (record) => {
    navigate(`/assets/maintenance/view?id=${record._id}`, { state: { maintenanceData: record } })
  }

  const handleDelete = (record) => {
    setDeleteItem(record)
    setShowDeleteModal(true)
  }

  const confirmDelete = async () => {
    if (!deleteItem) return

    try {
      toast.info('Deleting maintenance record...')
      const response = await assetMaintenanceService.deleteMaintenance(deleteItem._id)
      if (response.success) {
        toast.success('Maintenance record deleted successfully!', { autoClose: 2000 })
        loadData()
      } else {
        toast.error(response.message || 'Failed to delete maintenance record')
      }
    } catch (error) {
      console.error('Error deleting maintenance record:', error)
      if (error.response?.status === 404) {
        toast.error('Maintenance record not found')
      } else if (!error._errorHandled) {
        toast.error('Failed to delete maintenance record. Please try again.')
      }
    } finally {
      setShowDeleteModal(false)
      setDeleteItem(null)
    }
  }

  const cancelDelete = () => {
    setShowDeleteModal(false)
    setDeleteItem(null)
  }

  const handleExport = () => {
    toast.info('Export functionality coming soon!')
  }

  return (
    <div className="sales-dashboard">
      <Breadcrumb
        items={[
          { label: 'Home', path: '/' },
          { label: 'Assets', path: '/assets' },
          { label: 'Maintenance', isCurrent: true }
        ]}
      />

      <div className="page-header">
        <div className="page-title">
          <h1>Asset Maintenance</h1>
          <p>Asset maintenance and repairs</p>
        </div>
        <div className="page-actions">
          <button type="button" className="btn-primary" onClick={handleAdd}>
            <Plus size={16} />
            New Maintenance
          </button>
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={handleExport}>
            <Download size={16} />
            Export
          </button>
        </div>
      </div>

      <div className="dashboard-metrics-grid">
        <div className="metric-card deals">
          <div className="metric-header">
            <div className="metric-icon">
              <Wrench size={16} />
            </div>
            <div className="metric-trend positive">
              <span>Total</span>
            </div>
          </div>
          <div className="metric-value">{stats.totalMaintenance || 0}</div>
          <div className="metric-title">Total Records</div>
          <div className="metric-subtitle">All maintenance</div>
        </div>

        <div className="metric-card churn">
          <div className="metric-header">
            <div className="metric-icon">
              <Clock size={16} />
            </div>
            <div className="metric-trend neutral">
              <span>Open</span>
            </div>
          </div>
          <div className="metric-value">{(stats.scheduledCount || 0) + (stats.inProgressCount || 0)}</div>
          <div className="metric-title">Scheduled / In Progress</div>
          <div className="metric-subtitle">Requires attention</div>
        </div>

        <div className="metric-card revenue">
          <div className="metric-header">
            <div className="metric-icon">
              <CheckCircle size={16} />
            </div>
            <div className="metric-trend positive">
              <span>Done</span>
            </div>
          </div>
          <div className="metric-value">{stats.completedCount || 0}</div>
          <div className="metric-title">Completed</div>
          <div className="metric-subtitle">Serviced assets</div>
        </div>

        <div className="metric-card customers">
          <div className="metric-header">
            <div className="metric-icon">
              <DollarSign size={16} />
            </div>
            <div className="metric-trend positive">
              <span>Spend</span>
            </div>
          </div>
          <div className="metric-value">{formatAmount(stats.totalCost)}</div>
          <div className="metric-title">Total Cost</div>
          <div className="metric-subtitle">Maintenance spend</div>
        </div>
      </div>

      <div className="data-table-section">
        <DataTable
          data={data}
          columns={columns}
          loading={loading}
          onEdit={handleEdit}
          onView={handleView}
          onDelete={handleDelete}
          searchable={true}
          searchPlaceholder="Search maintenance records..."
          noCard={true}
          pageSize={10}
        />
      </div>

      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={cancelDelete}
        onConfirm={confirmDelete}
        title="Delete Maintenance Record"
        message={`Are you sure you want to delete maintenance "${deleteItem?.maintenanceCode}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />
    </div>
  )
}

export default AssetMaintenanceList
